import { Elements } from '@stripe/react-stripe-js';
import React from 'react';
import { useTheme } from 'styled-components';

import { CURRENCIES } from '@savgroup-front-common/constants';
import { BaseLoader } from '@savgroup-front-common/core/molecules/BaseLoader';
import { rgba } from '@savgroup-front-common/primitive/helpers/cssColor';
import { rem } from '@savgroup-front-common/primitive/helpers/units';

import CheckoutForm from './CheckoutForm/CheckoutForm';
import usePaymentStripe from './PaymentStripe.hooks';

interface PaymentStripeProps {
  onSubmitSuccess: () => Promise<void>;
  isFormCompleted: boolean;
  invoice: { id: string; priceWithTax: number; currencyTrigram: CURRENCIES };
  claimGroupId: string;
}

const PaymentStripe: React.FC<PaymentStripeProps> = ({
  onSubmitSuccess,
  isFormCompleted,
  invoice,
  claimGroupId,
}) => {
  const theme = useTheme();
  const { stripePromise, paymentIntentClientSecret, isLoading } =
    usePaymentStripe({
      invoiceId: invoice?.id,
      claimGroupId,
      onSubmitSuccess,
    });

  const appearance = {
    rules: {
      '.Input': {
        padding: `${rem(14)} ${rem(18)}`,
        outline: 'none',
        border: `${rem(1)} solid`,
        borderColor: theme.colors.default,
        borderRadius: rem(5),
        lineHeight: '1.15',
        boxShadow: 'unset',
        fontSize: theme.fonts.size.small,
        color: theme.colors.mainTextColor,
        fontFamily: 'Inter,Roboto,Arial,Helvetica,sans-serif',
      },
      '.Input:focus': {
        boxShadow: `0px 0px 4px ${rgba(theme.colors.primary, 0.4)}`,
        borderColor: `${theme.colors.primary}`,
      },
      '.Label': {
        fontSize: theme.fonts.size.small,
        fontWeight: theme.fonts.weight.bold.toString(),
        lineHeight: '16px',
        color: theme.newUI.defaultColors.secondaryText,
        margin: `0 0 ${rem(7)} 0`,
        fontFamily: 'Inter,Roboto,Arial,Helvetica,sans-serif',
      },
    },
  };

  if (isLoading || !stripePromise) {
    return <BaseLoader />;
  }

  return (
    <div>
      {paymentIntentClientSecret && (
        <Elements
          stripe={stripePromise}
          options={{ appearance, clientSecret: paymentIntentClientSecret }}
        >
          <CheckoutForm
            onSubmitSuccess={onSubmitSuccess}
            isFormCompleted={isFormCompleted}
            invoice={invoice}
          />
        </Elements>
      )}
    </div>
  );
};

export default PaymentStripe;
